import { Component, inject, OnInit, effect } from '@angular/core';
import { Router } from "@angular/router";
import { CommonModule } from '@angular/common';

import { Entity } from '../../objects/entity';
import { MatDialog } from '@angular/material/dialog';

import { CriticalDatesService } from '../../service/entity-services/critical-dates.service';
import { MyWorkFilterService, MyWorkFilter } from '../../service/my-work-filter.service';
import { UsersService } from '../../service/entity-services/users.service';

import { EntityType } from '../../objects/interface-library';
import { CriticalDate } from '../../objects/critical-dates';
import { hasEntityRoute, routeSegmentForEntityType } from '../../shared/utils/entity-route';
import { User } from '../../objects/user';

import { CriticalDateActionsDialogComponent } from '../../toolbar-components/critical-dates/critical-date-actions-dialog/critical-date-actions-dialog.component';
import { NewCriticalDateComponent } from '../../create-new/new-critical-date/new-critical-date.component';

import { FormsModule } from '@angular/forms';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';


@Component({
    selector: 'app-date-list',
    templateUrl: './date-list.component.html',
    styleUrls: ['./date-list.component.css'],
    standalone: true,
    imports: [
      CommonModule,
      FormsModule,
      MatProgressBar,
      MatButtonToggleModule,
      MatTableModule,
      MatSortModule,
      MatIconModule,
      MatChipsModule,
      MatTooltipModule
    ]
  })
export class DateListComponent extends Entity implements OnInit {

  public filterService = inject(MyWorkFilterService);

  public allDates: CriticalDate[] = [];
  public filteredDates: CriticalDate[] = [];
  private allUsers: User[] = [];

  waitingForFilterResults = false;

  // Expose MyWorkFilter enum to template
  MyWorkFilter = MyWorkFilter;

  // Entity type display name
  entityTypeName = 'Important Dates';

  displayedColumns: string[] = [
    'status',
    'name',
    'eventDate',
    'reminderDate',
    'assignedTo',
    'owner',
    'entityName',
    'entityType',
    'requiredAction'
  ];

  constructor(
    private router: Router
  ) {
    super();

    // Set up effect to watch for filter changes and re-filter dates
    effect(() => {
      // Read signals to establish dependencies
      const activeFilters = this.filterService.activeFilters();

      // Apply filtering whenever filters or dates change
      if (this.allDates.length > 0) {
        this.applyFilters();
      }
    });
  }

  async ngOnInit() {
    // Subscribe to users for name hydration
    this.usersService.allUsers$.subscribe(users => {
      this.allUsers = users || [];
    });

    // Subscribe to all visible critical dates from service
    this.criticalDatesService.visibleCriticalDates$.subscribe(dates => {
      this.allDates = dates;
      this.applyFilters();
    });
  }

  private applyFilters(): void {
    this.filteredDates = this.filterService.filterCriticalDates(this.allDates);
  }

  userChangedFilter(event: any): void {
    const selectedValues: string[] = event.value || [];
    this.filterService.clearFilters();
    selectedValues.forEach(value => {
      switch (value) {
        case 'PendingMyAction':
          this.filterService.addFilter(MyWorkFilter.PendingMyAction);
          break;
        case 'TaskingIAssigned':
          this.filterService.addFilter(MyWorkFilter.TaskingIAssigned);
          break;
      }
    });
  }

  isFilterActive(filter: MyWorkFilter): boolean {
    return this.filterService.isFilterActive(filter);
  }

  onRowClicked(date: CriticalDate): void {
    const dialogRef = this.dialog.open(CriticalDateActionsDialogComponent, {
      width: '400px',
      data: {
        criticalDate: date,
        // Ask the resolver, don't just check the fields are populated: a type we
        // have no route for made navigateToEntity a no-op, so the user got a
        // "Go To Item" button that did nothing at all.
        hasEntityLink: hasEntityRoute(date.entityType, date.entityNum || date.entityId)
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'edit') {
        this.openEditDialog(date);
      } else if (result === 'goto') {
        this.navigateToEntity(date);
      }
    });
  }

  private openEditDialog(criticalDate: CriticalDate): void {
    const dialogRef = this.dialog.open(NewCriticalDateComponent, {
      width: '800px',
      height: '600px',
      data: {
        title: 'Edit Important Date',
        message: 'Update the important date information',
        criticalDateData: { criticalDateData: criticalDate },
        entityObject: null
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('Critical date updated:', result);
      }
    });
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
    } catch {
      return dateString;
    }
  }

  formatAssignedTo(emails: string[] | undefined): string {
    if (!emails || !Array.isArray(emails) || emails.length === 0) return '';
    return emails.map(email => this.getUserName(email)).join('; ');
  }

  getUserName(email: string): string {
    if (!email) return '';
    const user = this.allUsers.find(u => u.email === email);
    if (!user) return email;
    if (user.lastName && user.firstName) return `${user.lastName}, ${user.firstName}`;
    return user.friendlyName || email;
  }

  isOverdue(date: CriticalDate): boolean {
    if (!date.eventDate || date.actionCompleted) return false;
    return new Date(date.eventDate) < new Date();
  }

  isDueSoon(date: CriticalDate): boolean {
    if (!date.eventDate || date.actionCompleted) return false;
    const eventDate = new Date(date.eventDate);
    const now = new Date();
    const daysUntil = (eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return daysUntil >= 0 && daysUntil <= 30;
  }

  sortData(sort: Sort): void {
    if (!sort.active || sort.direction === '') {
      this.applyFilters();
      return;
    }

    this.filteredDates = [...this.filteredDates].sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'name': return this.compare(a.name, b.name, isAsc);
        case 'eventDate': return this.compare(a.eventDate, b.eventDate, isAsc);
        case 'reminderDate': return this.compare(a.reminderDate, b.reminderDate, isAsc);
        case 'assignedTo': return this.compare(this.formatAssignedTo(a.assignedTo), this.formatAssignedTo(b.assignedTo), isAsc);
        case 'owner': return this.compare(a.owner, b.owner, isAsc);
        case 'entityName': return this.compare(a.entityName, b.entityName, isAsc);
        case 'entityType': return this.compare(a.entityType, b.entityType, isAsc);
        case 'requiredAction': return this.compare(a.requiredAction || a.description, b.requiredAction || b.description, isAsc);
        default: return 0;
      }
    });
  }

  private navigateToEntity(date: CriticalDate): void {
    const entityKey = date.entityNum || date.entityId;
    const route = routeSegmentForEntityType(date.entityType);
    if (!route || !entityKey) return;

    // Open in a new tab
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/securehome', route, entityKey])
    );
    window.open(url, '_blank');
  }

  private stripEntityPrefix(key: string): string {
    return key.replace(/^(CONTRACT-|BAA-|COI-|VENDOR-|PERSON-|SITE-|LEGAL_ENTITY-)/i, '');
  }

  private compare(a: any, b: any, isAsc: boolean): number {
    if (!a && !b) return 0;
    if (!a) return isAsc ? -1 : 1;
    if (!b) return isAsc ? 1 : -1;
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
}
